/**
 * Index Component Hook Validation Test
 * 
 * This is a specialized test component that wraps the Index component
 * to validate that our hook execution refactoring was successful.
 * It uses the hook testing utilities to track and verify consistent
 * hook execution across different view modes and loading states.
 */

import { useState, useRef } from 'react';
import { createHookExecutionContext } from '@/utils/hookTestUtils';
import Index from '@/pages/Index';

export function IndexComponentHookTest() {
  // Create context for tracking hook execution
  const contextRef = useRef(createHookExecutionContext());
  const context = contextRef.current;
  
  // Track what state we're currently testing
  const [currentTestState, setCurrentTestState] = useState<
    'initial' | 'daily-view' | 'weekly-view' | 'monthly-view' | 'loading'
  >('initial');
  
  // Track test results across states
  const [testResults, setTestResults] = useState<{
    [key: string]: {
      hookCount: number;
      orderConsistent: boolean;
      executionOrder: string[];
    }
  }>({});
  
  // Function to run the test for the current state
  const runTest = () => {
    // Get the current execution order
    const currentExecution = context.getExecutionOrder();
    
    // Compare with previous runs if any
    const previousStates = Object.keys(testResults);
    const orderConsistent = previousStates.length === 0 || 
      previousStates.every(state => {
        // Get the previous result for this state
        const prevResult = testResults[state];
        
        // Ensure prevResult exists
        if (!prevResult) {
          return false;
        }
        
        // Check hook count consistency
        if (prevResult.hookCount !== currentExecution.length) {
          return false;
        }
        
        // Check execution order consistency (only compare the hook declarations)
        const prevHooks = prevResult.executionOrder.filter(h => !h.includes(':callback') && !h.includes(':cleanup'));
        const currentHooks = currentExecution.filter(h => !h.includes(':callback') && !h.includes(':cleanup'));
        
        if (prevHooks.length !== currentHooks.length) {
          return false;
        }
        
        return prevHooks.every((hook, index) => hook === currentHooks[index]);
      });
    
    // Update test results
    setTestResults(prev => ({
      ...prev,
      [currentTestState]: {
        hookCount: currentExecution.length,
        orderConsistent,
        executionOrder: currentExecution
      }
    }));
    
    // Reset the context for the next test
    context.reset();
  };
  
  // Change the test state
  const changeTestState = (newState: typeof currentTestState) => {
    setCurrentTestState(newState);
  };
  
  return (
    <div className="p-6 border rounded-lg shadow-md bg-white">
      <h2 className="text-2xl font-bold mb-4">Index Component Hook Validation</h2>
      
      <div className="mb-6 p-4 bg-blue-50 rounded-lg">
        <h3 className="text-lg font-semibold mb-2">Current Test State: {currentTestState}</h3>
        <p className="text-sm text-gray-600 mb-4">
          Change the test state to verify hook execution consistency across different views and loading states.
        </p>
        
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => changeTestState('initial')}
            className={`px-3 py-1.5 rounded text-sm font-medium ${
              currentTestState === 'initial'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            }`}
          >
            Initial State
          </button>
          
          <button
            onClick={() => changeTestState('daily-view')}
            className={`px-3 py-1.5 rounded text-sm font-medium ${
              currentTestState === 'daily-view'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            }`}
          >
            Daily View
          </button>
          
          <button
            onClick={() => changeTestState('weekly-view')}
            className={`px-3 py-1.5 rounded text-sm font-medium ${
              currentTestState === 'weekly-view'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            }`}
          >
            Weekly View
          </button>
          
          <button
            onClick={() => changeTestState('monthly-view')}
            className={`px-3 py-1.5 rounded text-sm font-medium ${
              currentTestState === 'monthly-view'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            }`}
          >
            Monthly View
          </button>
          
          <button
            onClick={() => changeTestState('loading')}
            className={`px-3 py-1.5 rounded text-sm font-medium ${
              currentTestState === 'loading'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
            }`}
          >
            Loading State
          </button>
        </div>
        
        <div className="mt-4">
          <button
            onClick={runTest}
            className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
          >
            Run Test for Current State
          </button>
        </div>
      </div>
      
      {/* Test Results Display */}
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Test Results</h3>
        
        {Object.keys(testResults).length === 0 ? (
          <p className="text-gray-500 italic">No tests run yet</p>
        ) : (
          <div className="space-y-4">
            {Object.entries(testResults).map(([state, result]) => (
              <div
                key={state}
                className={`p-4 rounded-lg ${
                  result.orderConsistent
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-red-50 border border-red-200'
                }`}
              >
                <h4 className="font-medium flex items-center">
                  <span
                    className={`inline-block w-3 h-3 rounded-full mr-2 ${
                      result.orderConsistent ? 'bg-green-500' : 'bg-red-500'
                    }`}
                  />
                  {state} - {result.hookCount} hooks {result.orderConsistent ? '✅ Consistent' : '❌ Inconsistent'}
                </h4>
                
                <details className="mt-2">
                  <summary className="cursor-pointer text-sm text-gray-600">
                    View Execution Order
                  </summary>
                  <div className="mt-2 pl-2 border-l-2 border-gray-300 text-xs">
                    <ul className="space-y-1">
                      {result.executionOrder.map((hook, index) => (
                        <li
                          key={index}
                          className={`py-0.5 ${
                            hook.includes(':cleanup')
                              ? 'text-red-600'
                              : hook.includes(':callback')
                                ? 'text-blue-600'
                                : 'text-green-600'
                          }`}
                        >
                          {index + 1}. {hook}
                        </li>
                      ))}
                    </ul>
                  </div>
                </details>
              </div>
            ))}
            
            <div className="pt-4 border-t">
              <h4 className="font-medium mb-2">Summary</h4>
              <p>
                {Object.values(testResults).every(r => r.orderConsistent) ? (
                  <span className="text-green-600 font-medium">
                    ✅ All tests passed! Hook execution order is consistent across all tested states.
                  </span>
                ) : (
                  <span className="text-red-600 font-medium">
                    ❌ Issues detected! Hook execution order is inconsistent across tested states.
                  </span>
                )}
              </p>
            </div>
          </div>
        )}
      </div>
      
      {/* Component Under Test - wrapped with execution context */}
      <div className="border p-4 rounded-lg bg-gray-50">
        <h3 className="text-lg font-semibold mb-2">Component Under Test</h3>
        <p className="text-sm text-gray-500 mb-4">
          The Index component is rendered below with hook execution tracking enabled.
          The {currentTestState} state is currently active.
        </p>
        
        <div className="relative">
          {/* This would be the actual Index component with hook tracking applied */}
          <div className="opacity-30 pointer-events-none">
            {/* We're mocking this since injecting the context would require modifying the Index component */}
            <div className="p-4 border rounded bg-white">
              <h4 className="text-xl font-bold">Index Component (Mock)</h4>
              <p>View Mode: {currentTestState}</p>
              <p>Current state prevents interaction with component during testing</p>
            </div>
          </div>
          
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-white p-4 rounded shadow-md">
              <p className="text-amber-600">
                To fully implement this test, the Index component would need to accept a
                hook execution context prop. For now, this test is a demonstration.
              </p>
            </div>
          </div>
        </div>
      </div>
      
      <div className="mt-6 p-4 border rounded bg-amber-50">
        <h3 className="text-lg font-semibold mb-2">Implementation Notes</h3>
        <p className="text-sm">
          For a complete implementation, you would need to:
        </p>
        <ol className="list-decimal pl-5 mt-2 space-y-1 text-sm">
          <li>Modify the Index component to accept a hookExecutionContext prop</li>
          <li>Instrument each hook in the Index component with the context.recordExecution() call</li>
          <li>Pass the context to any child components that use hooks</li>
          <li>Add a testMode prop to prevent actual data fetching during tests</li>
        </ol>
        <p className="text-sm mt-2">
          Alternatively, for a non-invasive approach, you could use the useHookConsistencyCheck hook
          in a development-only wrapper of the Index component.
        </p>
      </div>
    </div>
  );
}
